import React, { useState } from 'react';
import { useResumeCountdown, useRoll, useRunControls } from '@root/hooks';
import { exitToLobby, outcomeTotals } from '@root/lib';
import { balanceSelector } from '@root/store/slices/balanceSlice';
import { fundingSelector, isRunPaused } from '@root/store/slices/fundingSlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import { useImageUrl } from '@shared/components/ImageContext';
import { formatMoney } from '@shared/lib';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import RunOutcomeDialog from '../RunOutcomeDialog';
import styles from './RunPausedModal.module.scss';

const pad = (value: number): string => String(value).padStart(2, '0');

// Can't fund the next roll: top up, abandon, or let the hour run out.
const RunPausedModal: React.FC = () => {
  const { t } = useTranslation();
  const { status, shortfallAmount, resumeDeadline, resuming, dialogDismissed } =
    useTypedSelector(fundingSelector);
  const paused = useTypedSelector(isRunPaused);
  const { balance } = useTypedSelector(balanceSelector);
  const { round } = useTypedSelector(roundSelector);
  const { rollAmount } = useRoll();
  const { resume, abandon } = useRunControls();
  const {
    clearFunding,
    dismissFundingDialog,
    setMenuPopupOpen,
    setMyBetsHistoryModalOpen,
  } = useActions();
  const [confirmingAbandon, setConfirmingAbandon] = useState(false);
  const { minutes, seconds } = useResumeCountdown(resumeDeadline);
  const { img, imgProps } = useImageUrl();

  const closed = status === 'ABANDONED' || status === 'EXPIRED';
  if (!closed && (!paused || dialogDismissed)) return null;

  const frame = {
    '--panel-skin': `url(${img('PopupFrame')})`,
  } as React.CSSProperties;
  const primarySkin = {
    '--button-skin': `url(${img('ButtonSkin_Primary')})`,
  } as React.CSSProperties;
  const secondarySkin = {
    '--button-skin': `url(${img('ButtonSkin_Secondary')})`,
  } as React.CSSProperties;

  const symbol = balance.currencySymbol;
  const money = (amount: number): string => formatMoney(symbol, amount);

  // The server's figure wins; before it lands we work it out ourselves.
  const needed = shortfallAmount ?? Math.max(0, rollAmount - balance.amount);
  // The hour starts at the refusal, so show a full one until it lands.
  const left = resumeDeadline ? `${pad(minutes)}:${pad(seconds)}` : '60:00';
  const expiry = resumeDeadline
    ? new Date(resumeDeadline).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  const canAfford = balance.amount >= rollAmount;

  // A run that ends this way never reached the finish.
  const totals = outcomeTotals(round?.state, false);
  const won = totals.won;

  const rows: [string, string][] = [
    [t('Next paid roll'), money(rollAmount)],
    [t('Available balance'), money(balance.amount)],
    [t('Amount needed'), money(needed)],
  ];

  const Row = ([label, value]: [string, string]) => (
    <div className={styles.row} key={label}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );

  return (
    <div className={styles.scrim}>
      <div className={styles.dialog} style={frame}>
        {closed && (
          <RunOutcomeDialog
            title={
              status === 'ABANDONED'
                ? t('Round abandoned')
                : t('Resume time expired')
            }
            emblem="RoundExpired"
            headline={t('LOSS')}
            won={false}
            totals={totals}
            note={
              status === 'ABANDONED'
                ? t(
                    'You chose to end this round. Previously credited rewards remain yours.'
                  )
                : t(
                    'You did not resume within 60 minutes. Previously credited rewards remain yours.'
                  )
            }
            symbol={symbol}
            onNewRound={() => clearFunding()}
            onViewDetails={() => setMyBetsHistoryModalOpen(true)}
            onMenu={() => setMenuPopupOpen(true)}
          />
        )}

        {!closed && confirmingAbandon && (
          <>
            <div className={styles.head}>
              <h2 className={styles.title}>{t('Abandon this round?')}</h2>
              <button
                type="button"
                className={styles.close}
                aria-label={t('Close')}
                onClick={() => setConfirmingAbandon(false)}
              >
                <img {...imgProps('Icon_Close')} alt="" />
              </button>
            </div>

            <div className={styles.status}>
              <img {...imgProps('RoundPaused')} alt="" />
            </div>
            <p className={styles.banner}>{t('Ends as a loss')}</p>

            <p className={styles.copy}>
              {t(
                'End the round now as a loss. Your {{rewards}} in credited rewards stays paid. Spent amounts are not refunded.',
                { rewards: money(won) }
              )}
            </p>

            <button
              type="button"
              className={cn(styles.skin, styles.skin_primary)}
              style={primarySkin}
              onClick={() => setConfirmingAbandon(false)}
            >
              {t('KEEP ROUND PAUSED')}
            </button>
            <button
              type="button"
              className={cn(styles.skin, styles.skin_secondary)}
              style={secondarySkin}
              onClick={() => abandon()}
            >
              {t('ABANDON · LOSS')}
            </button>
          </>
        )}

        {!closed && !confirmingAbandon && (
          <>
            <div className={styles.head}>
              <h2 className={styles.title}>
                {canAfford
                  ? t('Ready to resume')
                  : resumeDeadline
                    ? t('Your round is paused')
                    : t('Not enough balance')}
              </h2>
              <button
                type="button"
                className={styles.close}
                aria-label={t('Close')}
                onClick={() => dismissFundingDialog()}
              >
                <img {...imgProps('Icon_Close')} alt="" />
              </button>
            </div>

            <div className={styles.status}>
              <img {...imgProps('RoundPaused')} alt="" />
            </div>
            <p className={styles.banner}>
              {t('RESUME WITHIN {{left}}', { left })}
            </p>

            {rows.map(Row)}

            <p className={styles.copy}>
              {canAfford
                ? t(
                    'Your balance is ready. Select Resume Round before the deadline to keep playing.'
                  )
                : resumeDeadline
                  ? t(
                      'Your board, pawn position and roll amount are saved. Add balance, then select Resume Round before the timer ends.'
                    )
                  : t(
                      'Add balance and resume within 60 minutes, or this round will be abandoned as a loss.'
                    )}
            </p>
            {expiry && (
              <p className={styles.fine}>
                {t(
                  'Expires {{expiry}}. Closing or refreshing does not reset this time.',
                  { expiry }
                )}
              </p>
            )}

            {/* Leaving is the only route to a deposit. Resume once the
                balance covers a roll. */}
            {canAfford ? (
              <button
                type="button"
                className={cn(styles.skin, styles.skin_primary)}
                style={primarySkin}
                disabled={resuming}
                onClick={() => resume()}
              >
                {t('RESUME ROUND')}
              </button>
            ) : (
              <button
                type="button"
                className={cn(styles.skin, styles.skin_primary)}
                style={primarySkin}
                onClick={() => exitToLobby()}
              >
                {resumeDeadline ? t('ADD BALANCE') : t('PAUSE & ADD BALANCE')}
              </button>
            )}
            {!canAfford && resumeDeadline && (
              <button
                type="button"
                className={cn(styles.skin, styles.skin_secondary)}
                style={secondarySkin}
                disabled
              >
                {t('RESUME ROUND')}
              </button>
            )}
            <button
              type="button"
              className={cn(styles.skin, styles.skin_secondary)}
              style={secondarySkin}
              onClick={() => setConfirmingAbandon(true)}
            >
              {t('ABANDON ROUND')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RunPausedModal;
